/**
 * DxfActionsMenu — per-file action menu (rename category / delete / download).
 *
 * Rendered as a kebab trigger; the BOM export is intentionally surfaced
 * outside this menu as a first-class primary action on the canvas
 * floating action bar — see ``DxfPreviewStage`` + ``DxfBomButton``.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import { Download, FolderInput, MoreVertical, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@ando/ui";
import { confirm } from "@ando/ui/dialog";
import type { DxfFile } from "@ando/core";
import {
  useDeleteDxfFile,
  useUpdateDxfMetadata,
} from "../hooks/useDxfMutations";

export interface DxfActionsMenuProps {
  readonly file: DxfFile;
  readonly onDeleted?: () => void;
}

export function DxfActionsMenu({ file, onDeleted }: DxfActionsMenuProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(file.category ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const updateMutation = useUpdateDxfMetadata();
  const deleteMutation = useDeleteDxfFile();

  // Focus the rename field when the inline editor opens — the a11y-clean
  // alternative to the `autoFocus` attribute (which biome's noAutofocus bans).
  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  async function handleSaveCategory() {
    const value = pendingCategory.trim();
    await updateMutation.mutateAsync({
      id: file.content_item_id,
      body: { category: value === "" ? null : value },
    });
    setRenaming(false);
    setOpen(false);
  }

  async function handleDelete() {
    const ok = await confirm({
      title: i18n._(msg`Eliminare ${file.filename}?`),
      description: i18n._(msg`L'azione è irreversibile.`),
      confirmLabel: i18n._(msg`Elimina`),
      destructive: true,
    });
    if (!ok) return;
    await deleteMutation.mutateAsync(file.content_item_id);
    setOpen(false);
    onDeleted?.();
  }

  function handleDownload() {
    if (!file.download_url) return;
    const a = document.createElement("a");
    a.href = file.download_url;
    a.download = file.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-3 hover:bg-white/65 hover:text-ink-1"
          aria-label={i18n._(msg`Azioni sul disegno`)}
        >
          <MoreVertical size={14} strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom" sideOffset={4} className="w-[240px] p-1.5">
        {renaming ? (
          <div className="flex flex-col gap-2 p-2">
            <label
              htmlFor="dxf-category-input"
              className="text-[10px] text-ink-3 uppercase tracking-[0.05em]"
            >
              <Trans>Categoria</Trans>
            </label>
            <input
              id="dxf-category-input"
              ref={inputRef}
              type="text"
              value={pendingCategory}
              onChange={(e) => setPendingCategory(e.target.value)}
              placeholder={i18n._(msg`es. Cancelli, Tornelli…`)}
              className="w-full rounded-md border border-black/10 bg-white px-2 py-1.5 text-[12px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSaveCategory();
                if (e.key === "Escape") setRenaming(false);
              }}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleSaveCategory()}
                disabled={updateMutation.isPending}
                className="flex-1 rounded-md bg-ando-orange px-2 py-1.5 font-medium text-[11px] text-white hover:bg-ando-orange/90 disabled:opacity-60"
              >
                {updateMutation.isPending ? <Trans>Salvataggio…</Trans> : <Trans>Salva</Trans>}
              </button>
              <button
                type="button"
                onClick={() => setRenaming(false)}
                className="rounded-md px-2 py-1.5 text-[11px] text-ink-2 hover:bg-white/65"
              >
                <Trans>Annulla</Trans>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <MenuItem
              icon={<FolderInput size={13} strokeWidth={1.75} />}
              label={i18n._(msg`Cambia categoria`)}
              onClick={() => setRenaming(true)}
            />
            <MenuItem
              icon={<Download size={13} strokeWidth={1.75} />}
              label={i18n._(msg`Scarica originale`)}
              onClick={handleDownload}
              disabled={!file.download_url}
            />
            <div className="my-1 h-px bg-hairline" aria-hidden />
            <MenuItem
              icon={<Trash2 size={13} strokeWidth={1.75} />}
              label={i18n._(msg`Elimina`)}
              onClick={() => void handleDelete()}
              danger
              disabled={deleteMutation.isPending}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
DxfActionsMenu.displayName = "DxfActionsMenu";

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        danger
          ? "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[#B91C1C] text-[12px] hover:bg-[#FEE2E2] disabled:opacity-50"
          : "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-ink-2 hover:bg-white/65 hover:text-ink-1 disabled:opacity-50"
      }
    >
      <span aria-hidden className="shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}
